import { z } from 'zod/v4';

import {
    advancedBorderSettingsSchema,
    advancedRadiusSchema,
    analyticsSettingsPluginShapeLegacy,
    buttonAdditionalEffectsSchema,
    buttonIconSchema,
    buttonSizeSchema,
    buttonStyleTypeSchema,
    buttonWidthTypeSchema,
    createAnalyticsSettingsLegacy,
    createThemeButtonDataLegacy,
    createThemeButtonHoverLegacy,
    shadowPluginShapeLegacy,
    themeButtonDataSchemaLegacy,
    themeButtonHoverSchemaLegacy,
} from '../../../common/schemas/legacy';

/**
 * The fixed destination every EDU-login button points at. The widget never
 * lets the editor change this — the input in the settings panel is locked and
 * pre-filled with this value — so it lives here as the single source of truth
 * for both the entity factory default and the control's read-only display.
 */
export const EDU_LOGIN_BUTTON_LINK = '/courses/auth/login';

/**
 * Link kind, mirroring `EButtonLinkType`. Forked from the button widget: the
 * EDU-login button only ever uses `'link'`, but the shape is kept identical so
 * the data model can diverge independently later without a migration.
 */
export const eduLoginButtonLinkTypeSchema = z.enum(['email', 'phone', 'link', 'anchor']);

/**
 * One button of the widget's `buttons` array — a structural fork of the button
 * widget's `buttonItemSchema`. Kept intentionally identical so the two widgets
 * can evolve separately; the only behavioural difference lives in the factory
 * default below (`link.url` is seeded with {@link EDU_LOGIN_BUTTON_LINK}).
 */
export const eduLoginButtonItemSchema = z.object({
    label: z.string().nullable(),
    description: z.string().nullish(),
    styleType: buttonStyleTypeSchema,
    size: buttonSizeSchema,
    widthType: buttonWidthTypeSchema.nullish(),
    widthValue: z.number().nullish(),
    link: z.object({
        type: eduLoginButtonLinkTypeSchema,
        url: z.string().nullable(),
    }),
    isTargetBlank: z.boolean().nullish(),
    preventLinkConverting: z.boolean().nullish(),
    buttonBase: themeButtonDataSchemaLegacy,
    buttonHover: themeButtonHoverSchemaLegacy.nullish(),
    buttonActive: themeButtonDataSchemaLegacy.nullish(),
    buttonIcon: buttonIconSchema.nullish(),
    elementId: z.string().nullish(),
    isIndependentCorners: z.boolean().nullish(),
    additionalEffects: buttonAdditionalEffectsSchema.nullish(),
    ...analyticsSettingsPluginShapeLegacy,
    advancedRadius: advancedRadiusSchema.nullish(),
    advancedBorderSettings: advancedBorderSettingsSchema.nullish(),
    ...shadowPluginShapeLegacy,
});

export type EduLoginButtonItem = z.infer<typeof eduLoginButtonItemSchema>;

/**
 * Default EDU-login button item. Mirrors the button widget's `createButtonItem`
 * except the link is locked to {@link EDU_LOGIN_BUTTON_LINK}. As with the button
 * widget, `elementId` and `buttonIcon.icon` are seeded by the host app (cuid2 /
 * SVG template) via `overrides` since this package stays dependency-free.
 */
export const createEduLoginButtonItem = (overrides: Partial<EduLoginButtonItem> = {}): EduLoginButtonItem => ({
    label: 'Log in',
    description: null,
    styleType: 'primary',
    size: 'M',
    widthType: 'auto',
    widthValue: 260,
    link: {
        type: 'link',
        url: EDU_LOGIN_BUTTON_LINK,
    },
    isTargetBlank: false,
    preventLinkConverting: false,
    buttonBase: createThemeButtonDataLegacy(),
    buttonHover: createThemeButtonHoverLegacy(),
    buttonActive: null,
    buttonIcon: {
        isActive: false,
        icon: '',
        position: 'L',
    },
    elementId: '',
    isIndependentCorners: false,
    additionalEffects: {
        isActive: false,
        effectCondition: 'hover',
        hoverEffects: 'bounce',
        staticEffects: 'shine',
    },
    analyticsSettings: createAnalyticsSettingsLegacy(),
    advancedRadius: {
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
    },
    advancedBorderSettings: null,
    isShadow: false,
    shadowSettings: null,
    ...overrides,
});
